/**
 * Decide whether a route error surfaced by `useRouteError()` should be reported to Sentry.
 *
 * Expected client (4xx) route responses — e.g. a CMS `data-templates` miss that throws a
 * React Router 404 `ErrorResponse` — are rendered by RootErrorPage and are not actionable,
 * so they must NOT be captured (NGF-6626). We report only:
 *   - errors with no usable HTTP status (non-Response throws, e.g. a real `Error`), or
 *   - genuine server errors (status >= 500).
 *
 * Fail safe: a status that is absent or not a finite number is treated as "capture" rather
 * than "suppress", so a genuine error carrying a malformed/non-numeric `status` is still
 * reported instead of silently dropped. (`isErrorWithStatus` only asserts the `status` key
 * exists, not that it is a number, so the caller may pass a non-number here.)
 *
 * @param errorStatus the HTTP status resolved from the error, or `null`/a non-number when the
 *   error is not a status-bearing Response (or carries a malformed status).
 */
export function shouldCaptureRouteError(errorStatus: number | null): boolean {
  if (typeof errorStatus !== 'number' || !Number.isFinite(errorStatus)) {
    return true;
  }
  return errorStatus >= 500;
}
